/**
 * Normalizer for the central usage barrier payload.
 *
 * Wire contract (HTTP 402):
 *   {
 *     error: 'quota_exceeded',
 *     metric: 'drive_storage',
 *     barrier_mode: 'block',
 *     used: 1234, limit: 1000, remaining: 0,
 *     upgrades: [{ product_code: 'drive_extra_10gb', label: '+10 GB', extra_quota: 10737418240, amount_huf: 2000 }],
 *     recorded: true
 *   }
 *
 * The same payload shape is also served as a plain status object (no `error`
 * key) for pre-emptive meters, so `normalizeQuota` accepts both.
 */

import { subscription_path } from '../../../routes'

export const QUOTA_ERROR = 'quota_exceeded'
export const QUOTA_STATUS = 402

// Barrier modes that let the request through: warn the user, never block them.
const NON_BLOCKING_MODES = ['off', 'log', 'monitor', 'warn', 'soft']

function toNumber(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function safeJson(raw) {
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

export function quotaUpgradePath(productCode) {
  const code = productCode == null ? '' : String(productCode)
  return code ? subscription_path({ highlight: code }) : subscription_path()
}

function normalizeUpgrade(raw) {
  if (!raw || typeof raw !== 'object') return null
  const productCode = raw.product_code ?? raw.productCode ?? null
  if (productCode == null && !raw.label) return null
  return {
    productCode: productCode == null ? null : String(productCode),
    label: raw.label || String(productCode ?? ''),
    extraQuota: toNumber(raw.extra_quota ?? raw.extraQuota),
    amountHuf: toNumber(raw.amount_huf ?? raw.amountHuf),
    path: quotaUpgradePath(productCode)
  }
}

export function isBlockingMode(barrierMode) {
  const mode = String(barrierMode ?? '').toLowerCase()
  if (!mode) return true
  return !NON_BLOCKING_MODES.includes(mode)
}

/**
 * Turn a raw quota payload into the shape every quota component expects.
 * Returns null for anything that is not a quota payload.
 */
export function normalizeQuota(raw) {
  const body = typeof raw === 'string' ? safeJson(raw) : raw
  if (!body || typeof body !== 'object') return null

  const metric = body.metric == null ? null : String(body.metric)
  if (!metric) return null

  const used = toNumber(body.used) ?? 0
  const limit = toNumber(body.limit)
  const explicitRemaining = toNumber(body.remaining)
  const remaining = explicitRemaining ?? (limit == null ? null : Math.max(limit - used, 0))
  const barrierMode = body.barrier_mode ?? body.barrierMode ?? null
  const upgrades = (Array.isArray(body.upgrades) ? body.upgrades : [])
    .map(normalizeUpgrade)
    .filter(Boolean)

  return {
    metric,
    barrierMode: barrierMode == null ? null : String(barrierMode),
    used,
    limit,
    remaining,
    upgrades,
    recorded: body.recorded ?? null,
    exceeded: limit != null && limit > 0 && used >= limit,
    blocking: body.error === QUOTA_ERROR && isBlockingMode(barrierMode),
    ratio: limit != null && limit > 0 ? used / limit : null,
    raw: body
  }
}

/**
 * Parse an error body into a normalized quota object.
 * `status` is optional and only used when the body omits the `error` key.
 */
export function parseQuotaError(payload, status) {
  const body = typeof payload === 'string' ? safeJson(payload) : payload
  if (!body || typeof body !== 'object') return null

  const looksLikeQuota = body.error === QUOTA_ERROR ||
    (Number(status) === QUOTA_STATUS && body.metric != null)
  if (!looksLikeQuota) return null

  const quota = normalizeQuota(body)
  if (!quota) return null
  if (body.error === QUOTA_ERROR) return quota
  return { ...quota, blocking: isBlockingMode(quota.barrierMode) }
}

/**
 * Parse a fetch Response without consuming the caller's body.
 * Resolves to null when the response is not a quota barrier.
 */
export async function parseQuotaResponse(res) {
  if (!res || res.status !== QUOTA_STATUS) return null
  try {
    const body = await (typeof res.clone === 'function' ? res.clone() : res).json()
    return parseQuotaError(body, res.status)
  } catch (e) {
    return null
  }
}

/** True for errors thrown by `side-utils.js` style fetch wrappers (err.status / err.body). */
export function parseQuotaException(err) {
  if (!err) return null
  return parseQuotaError(err.body ?? err.payload, err.status)
}

export default parseQuotaError
